import { useMemo } from 'react'
import { useWindowDimensions } from 'react-native'
import type { LayoutInfo } from './types'

export interface AutoPositionConfig {
  /**
   * Layout do elemento trigger/root
   */
  triggerLayout: LayoutInfo | null

  /**
   * Layout do conteúdo a ser posicionado
   */
  contentLayout: LayoutInfo | null

  /**
   * Lado preferido para posicionamento
   * @default 'bottom'
   */
  preferredSide?: 'top' | 'bottom' | 'left' | 'right'

  /**
   * Alinhamento preferido
   * @default 'middle'
   */
  preferredAlign?: 'start' | 'middle' | 'end'

  /**
   * Espaço entre o trigger e o conteúdo
   * @default 8
   */
  gap?: number

  /**
   * Margem mínima das bordas da tela
   * @default 16
   */
  screenPadding?: number

  /**
   * Largura fixa para posicionamento lateral (usado pelo tooltip)
   */
  fixedLateralWidth?: number

  /**
   * Se deve tentar otimizar a posição automaticamente
   * @default true
   */
  autoPosition?: boolean
}

export interface AutoPositionResult {
  side: 'top' | 'bottom' | 'left' | 'right'
  align: 'start' | 'middle' | 'end'
}

export function useAutoPosition({
  triggerLayout,
  contentLayout,
  preferredSide = 'bottom',
  preferredAlign = 'middle',
  gap = 8,
  screenPadding = 16,
  fixedLateralWidth,
  autoPosition = true,
}: AutoPositionConfig): AutoPositionResult {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions()

  return useMemo(() => {
    if (!autoPosition || !triggerLayout || !contentLayout) {
      return {
        side: preferredSide,
        align: preferredAlign,
      }
    }

    const HALF_DIVISOR = 2
    const triggerX = triggerLayout.x
    const triggerY = triggerLayout.y
    const triggerWidth = triggerLayout.width
    const triggerHeight = triggerLayout.height
    const contentHeight = contentLayout.height

    const getContentWidth = (
      side: 'top' | 'bottom' | 'left' | 'right'
    ): number => {
      if (fixedLateralWidth && (side === 'left' || side === 'right')) {
        return fixedLateralWidth
      }
      return contentLayout.width
    }

    const isPositionValid = (
      side: 'top' | 'bottom' | 'left' | 'right',
      align: 'start' | 'middle' | 'end'
    ): boolean => {
      let tooltipX = 0
      let tooltipY = 0
      const currentContentWidth = getContentWidth(side)

      switch (side) {
        case 'top':
          tooltipY = triggerY - contentHeight - gap
          break
        case 'bottom':
          tooltipY = triggerY + triggerHeight + gap
          break
        case 'left':
          tooltipX = triggerX - currentContentWidth - gap
          tooltipY = triggerY + (triggerHeight - contentHeight) / HALF_DIVISOR

          break
        case 'right':
          tooltipX = triggerX + triggerWidth + gap
          tooltipY = triggerY + (triggerHeight - contentHeight) / HALF_DIVISOR
          break
      }

      if (side === 'top' || side === 'bottom') {
        const triggerCenterX = triggerX + triggerWidth / HALF_DIVISOR

        switch (align) {
          case 'start':
            tooltipX = Math.max(
              screenPadding,
              Math.min(
                triggerX,
                screenWidth - currentContentWidth - screenPadding
              )
            )
            break
          case 'middle':
            {
              const idealLeft =
                triggerCenterX - currentContentWidth / HALF_DIVISOR
              tooltipX = Math.max(
                screenPadding,
                Math.min(
                  idealLeft,
                  screenWidth - currentContentWidth - screenPadding
                )
              )
            }
            break
          case 'end':
            tooltipX = Math.max(
              screenPadding,
              triggerX + triggerWidth - currentContentWidth
            )
            break
        }
      }

      if (side === 'left' || side === 'right') {
        switch (align) {
          case 'start':
            tooltipY = triggerY
            break
          case 'middle':
            tooltipY = triggerY + (triggerHeight - contentHeight) / HALF_DIVISOR
            break
          case 'end':
            tooltipY = triggerY + triggerHeight - contentHeight
            break
        }
      }

      const isVerticallyValid =
        tooltipY >= screenPadding &&
        tooltipY + contentHeight <= screenHeight - screenPadding

      let isHorizontallyValid: boolean

      if (side === 'left') {
        const availableSpace = triggerX - gap - screenPadding
        isHorizontallyValid = availableSpace >= currentContentWidth
      } else if (side === 'right') {
        const availableSpace =
          screenWidth - (triggerX + triggerWidth) - gap - screenPadding
        isHorizontallyValid = availableSpace >= currentContentWidth
      } else {
        isHorizontallyValid =
          tooltipX >= screenPadding &&
          tooltipX + currentContentWidth <= screenWidth - screenPadding
      }

      return isVerticallyValid && isHorizontallyValid
    }

    const getValidAlign = (
      side: 'top' | 'bottom' | 'left' | 'right',
      alignPreference: 'start' | 'middle' | 'end'
    ): 'start' | 'middle' | 'end' => {
      if (
        (side === 'left' || side === 'right') &&
        fixedLateralWidth !== undefined
      ) {
        return 'middle'
      }
      return alignPreference
    }

    const sides: Array<'top' | 'bottom' | 'left' | 'right'> = [
      'top',
      'bottom',
      'left',
      'right',
    ]
    const aligns: Array<'start' | 'middle' | 'end'> = ['start', 'middle', 'end']

    const validPreferredAlign = getValidAlign(preferredSide, preferredAlign)
    if (isPositionValid(preferredSide, validPreferredAlign)) {
      return {
        side: preferredSide,
        align: validPreferredAlign,
      }
    }

    for (const align of aligns) {
      const validAlign = getValidAlign(preferredSide, align)
      if (
        validAlign !== validPreferredAlign &&
        isPositionValid(preferredSide, validAlign)
      ) {
        return {
          side: preferredSide,
          align: validAlign,
        }
      }
    }

    for (const side of sides) {
      const validAlign = getValidAlign(side, preferredAlign)
      if (side !== preferredSide && isPositionValid(side, validAlign)) {
        return {
          side,
          align: validAlign,
        }
      }
    }

    for (const side of sides) {
      for (const align of aligns) {
        const validAlign = getValidAlign(side, align)
        if (isPositionValid(side, validAlign)) {
          return {
            side,
            align: validAlign,
          }
        }
      }
    }

    const spaceAbove = triggerY
    const spaceBelow = screenHeight - (triggerY + triggerHeight)
    const spaceLeft = triggerX
    const spaceRight = screenWidth - (triggerX + triggerWidth)

    const maxSpace = Math.max(spaceAbove, spaceBelow, spaceLeft, spaceRight)
    let fallbackSide: 'top' | 'bottom' | 'left' | 'right'

    if (maxSpace === spaceAbove) {
      fallbackSide = 'top'
    } else if (maxSpace === spaceBelow) {
      fallbackSide = 'bottom'
    } else if (maxSpace === spaceLeft) {
      fallbackSide = 'left'
    } else {
      fallbackSide = 'right'
    }

    let fallbackAlign: 'start' | 'middle' | 'end' = 'middle'
    if (fallbackSide === 'top' || fallbackSide === 'bottom') {
      const contentWidth = getContentWidth(fallbackSide)
      const triggerCenterX = triggerX + triggerWidth / HALF_DIVISOR
      const idealLeft = triggerCenterX - contentWidth / HALF_DIVISOR

      if (idealLeft < screenPadding) {
        fallbackAlign = 'start'
      } else if (idealLeft + contentWidth > screenWidth - screenPadding) {
        fallbackAlign = 'end'
      }
    }

    return {
      side: fallbackSide,
      align: getValidAlign(fallbackSide, fallbackAlign),
    }
  }, [
    triggerLayout,
    contentLayout,
    preferredSide,
    preferredAlign,
    gap,
    screenWidth,
    screenHeight,
    screenPadding,
    fixedLateralWidth,
    autoPosition,
  ])
}
